// frontend/src/components/planner/QuickAddModal.jsx
// Lightweight modal for quickly adding one-time forecast entries
import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, Tag, FileText } from 'lucide-react';
import api from '../../utils/api';

const ITEM_TYPES = [
  { value: 'bill', label: 'Bill/Expense', color: 'orange' },
  { value: 'income', label: 'Income', color: 'emerald' },
  { value: 'debt', label: 'Debt Payment', color: 'red' }
];

// Static color mapping to avoid dynamic Tailwind class names (PurgeCSS strips them)
const colorStyles = {
  orange: {
    headerBg: 'bg-orange-600',
    submitBg: 'bg-orange-600',
    submitHover: 'hover:bg-orange-700',
    pillBg: 'bg-orange-50',
    pillBorder: 'border-orange-300',
    pillText: 'text-orange-700',
  },
  emerald: {
    headerBg: 'bg-emerald-600',
    submitBg: 'bg-emerald-600',
    submitHover: 'hover:bg-emerald-700',
    pillBg: 'bg-emerald-50',
    pillBorder: 'border-emerald-300',
    pillText: 'text-emerald-700',
  },
  red: {
    headerBg: 'bg-red-600',
    submitBg: 'bg-red-600',
    submitHover: 'hover:bg-red-700',
    pillBg: 'bg-red-50',
    pillBorder: 'border-red-300',
    pillText: 'text-red-700',
  },
};

export default function QuickAddModal({
  onClose,
  onSuccess,
  prefilledDate = null,
  defaultBankAccountId = null
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  
  // Form state
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [itemType, setItemType] = useState('bill');
  const [categoryId, setCategoryId] = useState('');
  const [scheduledDate, setScheduledDate] = useState(prefilledDate || getTodayString());
  const [notes, setNotes] = useState('');

  function getTodayString() {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  // Load categories on mount
  useEffect(() => {
    async function loadCategories() {
      try {
        const response = await api.get('/api/household/categories');
        if (response.data.categories) {
          setCategories(response.data.categories);
        }
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    }
    loadCategories();
  }, []);

  // Update date if prefilled date changes
  useEffect(() => {
    if (prefilledDate) {
      setScheduledDate(prefilledDate);
    }
  }, [prefilledDate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!scheduledDate) {
      setError('Date is required');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: name.trim(),
        amount: amountNum,
        item_type: itemType,
        category_id: categoryId ? parseInt(categoryId, 10) : null,
        scheduled_date: scheduledDate,
        notes: notes.trim() || null,
        bank_account_id: defaultBankAccountId ? parseInt(defaultBankAccountId, 10) : null
      };

      const response = await api.post('/api/forecast', payload);

      if (response.data.success) {
        onSuccess();
      } else {
        setError(response.data.error || 'Failed to add item');
      }
    } catch (err) {
      console.error('Error creating item:', err);
      setError(err.response?.data?.error || 'Failed to add item');
    } finally {
      setLoading(false);
    }
  }

  const selectedType = ITEM_TYPES.find(t => t.value === itemType);
  const styles = colorStyles[selectedType?.color] || colorStyles.emerald;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
      <div className="fixed inset-0" onClick={onClose} />

        <div className="relative bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-md sm:mx-4 sm:rounded-xl rounded-t-2xl overflow-hidden flex flex-col shadow-xl">
          {/* Header */}
          <div className={`sticky top-0 ${styles.headerBg} px-4 sm:px-5 py-4 flex-shrink-0 z-10`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Quick Add</h3>
                <p className="text-base sm:text-sm text-white/80">Add a one-time item to your forecast</p>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -mr-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-base sm:text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Item Type - Radio pills */}
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <div className="flex gap-2">
                {ITEM_TYPES.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setItemType(type.value)}
                    className={`
                      flex-1 px-3 py-2.5 sm:py-2 text-base sm:text-sm font-medium rounded-lg border transition-all min-h-[44px]
                      ${itemType === type.value
                        ? `${colorStyles[type.color].pillBg} ${colorStyles[type.color].pillBorder} ${colorStyles[type.color].pillText}`
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      }
                    `}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Car repair, Bonus, Gift"
                className="w-full px-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                autoFocus
              />
            </div>

            {/* Amount and Date - Side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Amount */}
              <div>
                <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                  Amount
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    placeholder="0.00"
                    className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none bg-white"
                >
                  <option value="">No category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                Notes <span className="text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add a reminder or note..."
                  rows={2}
                  className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] text-base sm:text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`w-full sm:w-auto px-4 py-3 sm:py-2 ${styles.submitBg} text-white rounded-lg ${styles.submitHover} disabled:opacity-50 transition-colors min-h-[44px] text-base sm:text-sm font-medium`}
              >
                {loading ? 'Adding...' : 'Add to Forecast'}
              </button>
            </div>
          </form>
        </div>
    </div>
  );
}
